import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Branding from './Branding';
import FeatureTiers from './FeatureTiers';
import { loadStripe } from '@stripe/stripe-js';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured

// Initialize Stripe - we'll load this asynchronously
let stripePromise = null;

const getStripe = () => {
  if (!stripePromise) {
    // This should be your Stripe publishable key
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_publishable_key');
  }
  return stripePromise;
};

const UpgradePage = ({ user, userTier }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [isFamilyPlan, setIsFamilyPlan] = useState(false);
  const [billingFrequency, setBillingFrequency] = useState('annual'); // 'annual' or 'monthly'
  const [processingMessage, setProcessingMessage] = useState('');
  const [isInFamilyPlan, setIsInFamilyPlan] = useState(false);
  const [familyPlanChecked, setFamilyPlanChecked] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get tier from URL query parameters and check for source parameter
  const [isFromSignup, setIsFromSignup] = useState(false);
  const [sourceContext, setSourceContext] = useState(null);
  const [guestTokens, setGuestTokens] = useState(0);
  const [sessionInteractions, setSessionInteractions] = useState(0);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tierParam = params.get('tier');
    const sourceParam = params.get('source');
    const tokensParam = params.get('tokens');
    const interactionsParam = params.get('session_interactions');
    
    // Set the selected tier if provided in URL
    if (tierParam && ['unlimited', 'home'].includes(tierParam)) {
      if (tierParam === 'home') {
        setSelectedTier('home');
        setIsFamilyPlan(true); // Home is a family plan
      } else {
        setSelectedTier(tierParam);
      }
    } else if (!selectedTier) {
      // Default to unlimited tier for new signups (simplified pricing)
      setSelectedTier('unlimited');
    }
    
    // Set guest context data from URL parameters
    if (tokensParam) {
      setGuestTokens(parseInt(tokensParam) || 0);
    }
    if (interactionsParam) {
      setSessionInteractions(parseInt(interactionsParam) || 0);
    }
    if (sourceParam) {
      setSourceContext(sourceParam);
    }
    
    // Check if coming from signup flow
    if (sourceParam === 'signup') {
      setIsFromSignup(true);
      // Track in PostHog
      try {
        posthog.capture('viewed_upgrade_after_signup', {
          selected_tier: selectedTier || tierParam || 'student'
        });
      } catch (err) {
        console.error('PostHog tracking error:', err);
      }
    }
    
    // Track guest conversion flow in PostHog
    if (sourceParam && ['token_store', 'progress_protection', 'token_store_unlock'].includes(sourceParam)) {
      try {
        posthog.capture('guest_conversion_flow_started', {
          source: sourceParam,
          tokens_earned: parseInt(tokensParam) || 0,
          session_interactions: parseInt(interactionsParam) || 0,
          selected_tier: tierParam
        });
      } catch (err) {
        console.error('PostHog tracking error:', err);
      }
    }
  }, [location]);
  
  // Check if user is already in a family plan
  useEffect(() => {
    const checkFamilyPlanStatus = async () => {
      if (!user) return;
      
      try {
        // Check if user has expert role (might indicate family plan membership)
        if (user.role === 'expert' && userTier === 'expert') {
          // User has expert access but we need to verify if it's through family plan
          // For now, assume expert users from signup are family plan members
          setIsInFamilyPlan(true);
        }
        setFamilyPlanChecked(true);
      } catch (error) {
        console.error('Error checking family plan status:', error);
        setFamilyPlanChecked(true);
      }
    };
    
    checkFamilyPlanStatus();
  }, [user, userTier]);
  
  // Get tier data
  const tierData = FeatureTiers.getTierComparisonData();
  
  // Find current tier price and selected tier price
  const getCurrentTierPrice = (tier) => {
    const tierInfo = tierData.tiers.find(t => t.id === tier);
    return tierInfo ? tierInfo.price : 'Free';
  };
  
  // Get the display price for selected tier (accounting for family plan)
  const getSelectedTierPrice = () => {
    if (!selectedTier) return 'Free';
    const tierInfo = tierData.tiers.find(t => t.id === selectedTier);
    if (!tierInfo) return 'Free';
    
    if (isFamilyPlan && tierInfo.familyPlan) {
      return `$${tierInfo.familyPlan.price}/year`;
    }
    
    if (tierInfo.hasMonthlyOption && billingFrequency === 'monthly') {
      return tierInfo.monthlyPrice;
    }
    
    return tierInfo.price;
  };
  
  // Get personalized welcome message based on source context
  const getWelcomeMessage = () => {
    const tokenValue = guestTokens > 0 ? `$${(guestTokens * 0.10).toFixed(2)}` : '';
    
    switch(sourceContext) {
      case 'token_store':
        return {
          title: "Unlock Your Token Investment!",
          message: `You've earned ${guestTokens} tokens (worth ${tokenValue}). Upgrade to unlock unlimited earning potential and spend your tokens on academic tools.`,
          icon: "💰"
        };
      case 'progress_protection':
        return {
          title: "Protect Your Learning Investment!",
          message: `Don't lose your ${guestTokens} tokens and ${sessionInteractions} interactions! Create an account to save your progress and continue building your academic success.`,
          icon: "🛡️"
        };
      case 'token_store_unlock':
        return {
          title: "Transform Tokens Into Academic Success!",
          message: `Your ${guestTokens} tokens represent real learning investment. Upgrade to Premium to unlock the full token store and academic portfolio tools.`,
          icon: "🎓"
        };
      default:
        return {
          title: isFromSignup ? 'Complete Your Account Setup' : 'Upgrade Your Account',
          message: isFromSignup ? 
            'Your account has been created. Choose your subscription plan to unlock unlimited tutoring and premium features.' :
            'Choose your subscription plan to unlock unlimited tutoring and premium features.',
          icon: "🚀"
        };
    }
  };
  
  // Handle upgrade button click
  const handleUpgrade = async () => {
    if (!selectedTier) {
      setError('Please select a tier to upgrade to');
      return;
    }
    
    if (!user) {
      // Not logged in - redirect to login
      navigate('/login?mode=signup&redirect=' + encodeURIComponent('/upgrade?tier=' + selectedTier));
      return;
    }
    
    // Show loading state
    setLoading(true);
    setError(null);
    setProcessingMessage('Creating checkout session...');
    
    try {
      // Call our payment API to create a checkout session
      // FIXED: Include the user ID/username to prevent 'demo_user' fallback
      const tierForPayment = isFamilyPlan ? 'expert-family' : selectedTier;
      const billingForPayment = isFamilyPlan ? 'annual' : billingFrequency; // Family plans are always annual
      const response = await axios.post('/api/payment/create-checkout-session', {
        tier: tierForPayment,
        billing_frequency: billingForPayment
      }, {
        // FIXED: Include credentials for HttpOnly cookie authentication
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Get checkout URL from response
      const { checkout_url } = response.data;
      
      // Redirect to Stripe checkout
      setProcessingMessage('Redirecting to secure payment page...');
      window.location.href = checkout_url;
      
    } catch (error) {
      console.error('Error creating checkout session:', error);

      // User-friendly error messages based on error type
      const status = error.response?.status;
      const serverError = error.response?.data?.error;

      let userMessage;
      if (status === 401) {
        // Session expired - need to log in again
        userMessage = 'Your session has expired. Please log in again to continue.';
        // Redirect to login after a brief delay
        setTimeout(() => {
          navigate('/login?mode=login&redirect=' + encodeURIComponent('/upgrade?tier=' + selectedTier));
        }, 2000);
      } else if (status === 403) {
        userMessage = 'Access denied. Please make sure you have the right permissions.';
      } else if (status === 429) {
        userMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (serverError) {
        userMessage = serverError;
      } else if (!navigator.onLine) {
        userMessage = 'No internet connection. Please check your connection and try again.';
      } else {
        userMessage = 'Something went wrong. Please try again or contact support.';
      }

      setError(userMessage);
      setLoading(false);
      setProcessingMessage('');
    }
  };
  
  return (
    <div className="upgrade-page" style={{
      padding: Branding.spacing.xl,
      backgroundColor: Branding.colors.nearlyWhite,
      minHeight: '100vh'
    }}>
      <div className="upgrade-container" style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: Branding.colors.pureWhite,
        borderRadius: Branding.borders.radius.lg,
        boxShadow: Branding.shadows.md,
        padding: Branding.spacing.xl
      }}>
        <h1 style={{
          fontSize: Branding.typography.fontSize['3xl'],
          color: Branding.colors.darkSlate,
          marginBottom: isFromSignup ? Branding.spacing.sm : Branding.spacing.lg,
          textAlign: 'center'
        }}>
          {getWelcomeMessage().title}
        </h1>
        
        {/* Family Plan Member Message */}
        {familyPlanChecked && isInFamilyPlan && (
          <div style={{
            backgroundColor: Branding.colors.lightGreen || '#e6f7e6',
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.lg,
            marginBottom: Branding.spacing.lg,
            textAlign: 'center',
            border: '2px solid #4ade80'
          }}>
            <div style={{ 
              fontSize: '48px',
              marginBottom: Branding.spacing.md
            }}>
              ✅
            </div>
            <h2 style={{
              color: '#166534',
              fontSize: Branding.typography.fontSize.xl,
              fontWeight: Branding.typography.fontWeight.bold,
              marginBottom: Branding.spacing.sm
            }}>You're All Set!</h2>
            <p style={{
              color: '#166534',
              fontSize: Branding.typography.fontSize.base,
              marginBottom: Branding.spacing.md
            }}>
              You already have Expert access through a family plan. No additional subscription needed!
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: Branding.borders.radius.md,
                padding: `${Branding.spacing.sm} ${Branding.spacing.lg}`,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: 'pointer'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        )}
        
        {/* Welcome banner for users - personalized based on source */}
        {(isFromSignup || sourceContext) && (
          <div style={{
            backgroundColor: sourceContext === 'progress_protection' ? '#FFF8F0' : 
                           sourceContext === 'token_store' ? '#F0FDF4' :
                           sourceContext === 'token_store_unlock' ? '#EFF6FF' :
                           Branding.colors.lightTeal,
            borderRadius: Branding.borders.radius.md,
            padding: Branding.spacing.md,
            marginBottom: Branding.spacing.lg,
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            border: `1px solid ${sourceContext === 'progress_protection' ? '#F59E0B' : 
                                sourceContext === 'token_store' ? '#10B981' :
                                sourceContext === 'token_store_unlock' ? '#3B82F6' :
                                Branding.colors.tealBlue}`
          }}>
            <div style={{ 
              marginRight: Branding.spacing.md,
              color: sourceContext === 'progress_protection' ? '#F59E0B' : 
                     sourceContext === 'token_store' ? '#10B981' :
                     sourceContext === 'token_store_unlock' ? '#3B82F6' :
                     Branding.colors.tealBlue,
              fontSize: '24px'
            }}>
              {getWelcomeMessage().icon}
            </div>
            <div>
              <h3 style={{
                color: sourceContext === 'progress_protection' ? '#92400E' : 
                       sourceContext === 'token_store' ? '#047857' :
                       sourceContext === 'token_store_unlock' ? '#1E40AF' :
                       Branding.colors.darkTeal,
                fontSize: Branding.typography.fontSize.lg,
                fontWeight: Branding.typography.fontWeight.semibold,
                marginBottom: Branding.spacing.xs
              }}>You're Making Great Progress!</h3>
              <p style={{
                color: Branding.colors.darkSlate,
                fontSize: Branding.typography.fontSize.base,
                margin: 0
              }}>
                {getWelcomeMessage().message}
              </p>
            </div>
          </div>
        )}
        
        {/* Only show upgrade form if user is NOT in a family plan */}
        {familyPlanChecked && !isInFamilyPlan && (
          <>
        {/* Current plan section */}
        <div className="current-plan" style={{
          marginBottom: Branding.spacing.xl,
          padding: Branding.spacing.lg,
          borderRadius: Branding.borders.radius.md,
          backgroundColor: Branding.colors.offWhite,
          border: `1px solid ${Branding.colors.veryLightGray}`
        }}>
          <h2 style={{
            fontSize: Branding.typography.fontSize.xl,
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md
          }}>Current Plan</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: Branding.spacing.md,
            backgroundColor: Branding.colors.pureWhite,
            borderRadius: Branding.borders.radius.md,
            boxShadow: Branding.shadows.sm
          }}>
            <div>
              <div style={{
                fontSize: Branding.typography.fontSize.lg,
                fontWeight: Branding.typography.fontWeight.bold,
                color: Branding.colors.darkSlate
              }}>
                {user && user.role === 'registered' ? 'Registered (Unsubscribed)' : FeatureTiers.formatTierName(userTier)}
              </div>
              <div style={{
                fontSize: Branding.typography.fontSize.sm,
                color: Branding.colors.slateGray,
                marginTop: Branding.spacing.xs
              }}>
                Current billing cycle
              </div>
            </div>
            
            <div style={{
              fontSize: Branding.typography.fontSize.lg,
              fontWeight: Branding.typography.fontWeight.bold,
              color: Branding.colors.tealBlue
            }}>
              {getCurrentTierPrice(userTier)}
            </div>
          </div>
        </div>
        
        {/* Selected plan section */}
        <div className="selected-plan" style={{
          marginBottom: Branding.spacing.xl
        }}>
          <h2 style={{
            fontSize: Branding.typography.fontSize.xl,
            color: Branding.colors.darkSlate,
            marginBottom: Branding.spacing.md
          }}>Selected Plan</h2>
          
          {/* Plan selector */}
          {/* Billing frequency selector - only show if selected tier has monthly option AND it's not family plan */}
          {selectedTier && !isFamilyPlan && (tierData.tiers.find(t => t.id === selectedTier)?.hasMonthlyOption) && (
            <div className="billing-frequency-selector" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: Branding.spacing.md,
              backgroundColor: Branding.colors.offWhite,
              padding: Branding.spacing.sm,
              borderRadius: Branding.borders.radius.lg
            }}>
              <div style={{
                fontSize: Branding.typography.fontSize.sm,
                fontWeight: Branding.typography.fontWeight.medium,
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.sm
              }}>
                Billing Frequency
              </div>
              <div style={{
                display: 'flex',
                borderRadius: Branding.borders.radius.md,
                overflow: 'hidden',
                border: `1px solid ${Branding.colors.tealBlue}`
              }}>
                <button
                  onClick={() => setBillingFrequency('monthly')}
                  style={{
                    padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                    backgroundColor: billingFrequency === 'monthly' 
                      ? Branding.colors.tealBlue
                      : Branding.colors.pureWhite,
                    color: billingFrequency === 'monthly'
                      ? Branding.colors.pureWhite
                      : Branding.colors.tealBlue,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: Branding.typography.fontWeight.medium,
                    fontSize: Branding.typography.fontSize.sm
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingFrequency('annual')}
                  style={{
                    padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                    backgroundColor: billingFrequency === 'annual'
                      ? Branding.colors.tealBlue
                      : Branding.colors.pureWhite,
                    color: billingFrequency === 'annual'
                      ? Branding.colors.pureWhite
                      : Branding.colors.tealBlue,
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: Branding.typography.fontWeight.medium,
                    fontSize: Branding.typography.fontSize.sm
                  }}
                >
                  Annual
                  <span style={{
                    marginLeft: Branding.spacing.xs, 
                    fontSize: Branding.typography.fontSize.xs,
                    backgroundColor: billingFrequency === 'annual' ? 'rgba(255, 255, 255, 0.2)' : Branding.colors.paleBlue,
                    padding: `0 ${Branding.spacing.xs}`,
                    borderRadius: Branding.borders.radius.sm
                  }}>
                    {selectedTier === 'home' ? 'SAVE 17%' : 'SAVE 26%'}
                  </span>
                </button>
              </div>
            </div>
          )}
          
          <div className="plan-selector" style={{
            display: 'flex',
            gap: Branding.spacing.md,
            marginBottom: Branding.spacing.lg
          }}>
            {tierData.tiers
              .filter(tier => ['unlimited', 'home'].includes(tier.id) && tier.id !== userTier)
              .map(tier => (
                <div 
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  style={{
                    flex: 1,
                    padding: Branding.spacing.md,
                    backgroundColor: selectedTier === tier.id 
                      ? (tier.id === 'home' 
                         ? '#F0FDF4' // Light green background for Home tier
                         : tier.id === 'unlimited' 
                           ? '#EFF6FF' // Light blue background for Unlimited tier
                           : Branding.colors.paleBlue)
                      : Branding.colors.pureWhite,
                    borderRadius: Branding.borders.radius.md,
                    border: `2px solid ${selectedTier === tier.id 
                      ? (tier.id === 'home'
                         ? '#10B981' // Green border for Home
                         : tier.id === 'unlimited' 
                           ? '#3B82F6' // Blue border for Unlimited 
                           : Branding.colors.tealBlue) 
                      : Branding.colors.veryLightGray}`,
                    boxShadow: selectedTier === tier.id ? Branding.shadows.md : Branding.shadows.sm,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{
                    fontSize: Branding.typography.fontSize.lg,
                    fontWeight: Branding.typography.fontWeight.bold,
                    color: Branding.colors.darkSlate,
                    marginBottom: Branding.spacing.xs
                  }}>
                    {tier.name}
                  </div>
                  
                  <div style={{
                    fontSize: Branding.typography.fontSize['2xl'],
                    fontWeight: Branding.typography.fontWeight.bold,
                    color: tier.id === 'home' 
                      ? '#10B981' // Green for Home
                      : tier.id === 'unlimited' 
                        ? '#3B82F6' // Blue for Unlimited 
                        : Branding.colors.tealBlue,
                    marginBottom: Branding.spacing.sm
                  }}>
                    {selectedTier === tier.id && isFamilyPlan && tier.familyPlan 
                      ? `$${tier.familyPlan.price}/year`
                      : tier.hasMonthlyOption && billingFrequency === 'monthly' && !isFamilyPlan
                        ? tier.monthlyPrice 
                        : tier.price}
                  </div>
                  
                  {/* Family plan indicator */}
                  {selectedTier === tier.id && isFamilyPlan && tier.familyPlan && (
                    <div style={{
                      fontSize: Branding.typography.fontSize.sm,
                      color: Branding.colors.darkSlate,
                      marginBottom: Branding.spacing.sm,
                      padding: `${Branding.spacing.xs} ${Branding.spacing.sm}`,
                      backgroundColor: 'rgba(234, 109, 55, 0.1)',
                      borderRadius: Branding.borders.radius.md,
                      border: `1px solid ${Branding.colors.vibrantOrange}`
                    }}>
                      👨‍👩‍👧‍👦 Family Plan ({tier.familyPlan.description})
                    </div>
                  )}
                  
                  {tier.hasMonthlyOption && (
                    <div style={{
                      fontSize: Branding.typography.fontSize.xs,
                      color: Branding.colors.slateGray,
                      marginBottom: Branding.spacing.sm
                    }}>
                      {billingFrequency === 'monthly' 
                        ? `Annual option: ${tier.price} (save ${tier.annualSavings}%)` 
                        : `Also available monthly: ${tier.monthlyPrice}`}
                    </div>
                  )}
                  
                  <div style={{
                    fontSize: Branding.typography.fontSize.sm,
                    color: Branding.colors.slateGray
                  }}>
                    {tier.description}
                  </div>
                </div>
              ))}
          </div>
          
          {/* Plan features */}
          {selectedTier && (
            <div className="plan-features" style={{
              padding: Branding.spacing.lg,
              backgroundColor: Branding.colors.offWhite,
              borderRadius: Branding.borders.radius.md,
              marginBottom: Branding.spacing.lg
            }}>
              <h3 style={{
                fontSize: Branding.typography.fontSize.lg,
                color: Branding.colors.darkSlate,
                marginBottom: Branding.spacing.md
              }}>
                {FeatureTiers.formatTierName(selectedTier)} Plan Includes:
              </h3>
              
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {tierData.tiers
                  .find(tier => tier.id === selectedTier)?.features
                  .map((feature, index) => (
                    <li 
                      key={index}
                      style={{
                        padding: `${Branding.spacing.sm} 0`,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{
                        color: Branding.colors.blueGreen,
                        marginRight: Branding.spacing.sm
                      }}>✓</span>
                      {feature}
                    </li>
                  ))}
                  
                {/* Limitations */}
                {tierData.tiers.find(tier => tier.id === selectedTier)?.limitations?.length > 0 && (
                  <>
                    <li style={{
                      padding: `${Branding.spacing.md} 0 ${Branding.spacing.sm}`,
                      fontWeight: Branding.typography.fontWeight.semibold,
                      color: Branding.colors.darkSlate,
                      marginTop: Branding.spacing.sm,
                      borderTop: `1px dashed ${Branding.colors.veryLightGray}`
                    }}>
                      Limitations:
                    </li>
                    {tierData.tiers
                      .find(tier => tier.id === selectedTier)?.limitations
                      .map((limitation, index) => (
                        <li 
                          key={`limit-${index}`}
                          style={{
                            padding: `${Branding.spacing.xs} 0`,
                            display: 'flex',
                            alignItems: 'center',
                            color: Branding.colors.mutedMauve
                          }}
                        >
                          <span style={{
                            color: Branding.colors.mutedMauve,
                            marginRight: Branding.spacing.sm
                          }}>✕</span>
                          {limitation}
                        </li>
                      ))}
                  </>
                )}
              </ul>
            </div>
          )}
        </div>
        
        {/* Error message */}
        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: Branding.borders.radius.md,
            color: '#b91c1c',
            padding: Branding.spacing.md,
            marginBottom: Branding.spacing.md,
            fontSize: Branding.typography.fontSize.sm
          }}>
            {error}
          </div>
        )}
        
        {/* Processing message */}
        {processingMessage && (
          <div style={{
            backgroundColor: '#e0f2fe',
            border: `1px solid ${Branding.colors.paleBlue}`,
            borderRadius: Branding.borders.radius.md,
            color: Branding.colors.tealBlue,
            padding: Branding.spacing.md,
            marginBottom: Branding.spacing.md,
            fontSize: Branding.typography.fontSize.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: Branding.spacing.sm
          }}>
            <div className="spinner" style={{
              width: '16px',
              height: '16px',
              border: `2px solid ${Branding.colors.tealBlue}`,
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            {processingMessage}
          </div>
        )}
        
        {/* Payment button */}
        <button
          onClick={handleUpgrade}
          disabled={loading || !selectedTier}
          style={{
            width: '100%',
            padding: Branding.spacing.md,
            backgroundColor: loading 
              ? Branding.colors.veryLightGray 
              : (selectedTier === 'home'
                 ? '#10B981' // Green for Home
                 : selectedTier === 'unlimited' 
                   ? '#3B82F6' // Blue for Unlimited 
                   : Branding.colors.tealBlue),
            color: Branding.colors.pureWhite,
            border: 'none',
            borderRadius: Branding.borders.radius.md,
            fontSize: Branding.typography.fontSize.lg,
            fontWeight: Branding.typography.fontWeight.semibold,
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: Branding.shadows.md,
            transition: 'all 0.2s ease'
          }}
        >
          {loading ? 'Processing...' : (isFromSignup ? `Complete Setup and Subscribe` : `Proceed to Payment`)}
        </button>
        
          </>
        )}
        
        {/* Secure payment note */}
        <div style={{
          marginTop: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.xs,
          color: Branding.colors.slateGray,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: Branding.spacing.xs
        }}>
          <span>🔒</span> Secure payment powered by Stripe
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;